import { useEffect, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useAllEventsQuery } from '../../../graphql/queries/allEvents.generated';
import { Event } from '../../../graphql/generatedTypes';
import { Routes } from '@/utils/routes';

import {
  ContextSidebarConfig,
  useRouteSidebar,
} from '@/components/SidebarContext';
import { useBreadcrumbs } from '@/components/Breadcrumbs/useBreadcrumbs';
import SvgIcon from '@/components/Icon/SvgIcon';
import { EventSwitcher } from './EventSwitcher';
import { EventOwnerFulfillments } from './EventOwnerFulfillments';
import { EventOwnerEventCards } from './EventOwnerEventCards';

const EventOwnerDashboard = () => {
  const params = useParams<{ eventId?: string }>();
  const location = useLocation();
  const history = useHistory();

  const { eventId } = params ?? {};

  const { loading: loadingEvents, data: eventsData } = useAllEventsQuery({
    errorPolicy: 'all',
  });

  const events = useMemo(
    () =>
      (eventsData?.events?.map((e) => ({
        ...e,
        image_url: e.image_url,
      })) || []) as Event[],
    [eventsData],
  );

  // Find the selected event based on URL parameter
  const selectedEvent = useMemo(() => {
    if (!eventId || !events.length) return undefined;
    return events.find((e) => e.id === eventId);
  }, [eventId, events]);

  // Auto-select first event if an invalid event is selected
  useEffect(() => {
    if (events.length === 0) {
      return;
    }
    if (eventId && !events.find((e) => e.id === eventId)) {
      history.replace(Routes.eventOwnerDashboard(events[0].id));
    }
  }, [eventId, events, history]);

  const handleEventSelection = (event: Event | null) => {
    if (!event) {
      history.push(Routes.eventOwnerDashboard());
    } else {
      history.push(Routes.eventOwnerDashboardFulfillments(event.id));
    }
  };

  const showLoading = loadingEvents || !events;

  const route: 'overview' | 'handouts-returns' = useMemo(() => {
    if (location.pathname.includes('/fulfillments')) {
      return 'handouts-returns';
    }
    return 'overview';
  }, [location.pathname]);

  // Feed the shared app-shell sidebar: the event switcher as the header slot,
  // and (once an event is picked) the dashboard's navigation as groups. The
  // shell owns the footer user menu, so there is no longer a bespoke sidebar.
  const sidebarConfig: ContextSidebarConfig = useMemo(
    () => ({
      headerKey: `${selectedEvent?.id ?? 'all-events'}-${events.length}`,
      header: (
        <EventSwitcher
          events={events}
          selectedEvent={selectedEvent}
          onEventSelect={handleEventSelection}
        />
      ),
      groups: selectedEvent
        ? [
            {
              links: [
                {
                  label: 'Handouts & Returns',
                  href: Routes.eventOwnerDashboardFulfillments(
                    selectedEvent.id ?? '',
                  ),
                  icon: <SvgIcon name="clipboard-list-search" size={16} />,
                },
              ],
            },
          ]
        : [],
    }),
    // header is rebuilt every render; headerKey/selectedEvent/events drive it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedEvent, events],
  );

  useRouteSidebar(sidebarConfig, { id: 'event-owner-dashboard' });

  useBreadcrumbs({
    id: 'event-owner-dashboard',
    priority: 100,
    items: [
      { label: 'All events', href: Routes.eventOwnerDashboard() },
      ...(selectedEvent?.name
        ? [
            {
              label: selectedEvent.name,
              href: Routes.eventOwnerDashboardFulfillments(selectedEvent.id),
              isCurrent: true,
            },
          ]
        : []),
    ],
  });

  return (
    <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-4 tw:pt-0">
      {!showLoading && route === 'handouts-returns' && (
        <EventOwnerFulfillments event={selectedEvent} />
      )}
      {route === 'overview' && (
        <EventOwnerEventCards
          events={events}
          isLoading={showLoading}
          onEventSelect={handleEventSelection}
        />
      )}
    </div>
  );
};

export default EventOwnerDashboard;
